import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Play, Pause, SkipBack, SkipForward, Heart, ExternalLink, ListMusic } from "lucide-react";
import { usePlayer } from "../contexts/PlayerContext";
import { useFavorites } from "../contexts/FavoritesContext";
import { formatDuration } from "../lib/api";
import AddToPlaylistButton from "./AddToPlaylistButton";

/** Lecteur plein écran : grande pochette, grosses commandes, liste « À suivre ».
 *  S'ouvre en touchant le mini-lecteur ; se ferme avec la flèche ou en glissant vers le bas. */
export default function FullPlayer() {
    const { current, playing, progress, duration, toggle, seek, playNext, playPrev, hasNext, hasPrev, queue, queueIndex, playAt, setExpanded } = usePlayer();
    const { isFavorite, toggle: toggleFav } = useFavorites();
    const touchY = useRef(null);
    const close = () => setExpanded(false);

    useEffect(() => {
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        const onKey = (e) => { if (e.key === "Escape") close(); };
        window.addEventListener("keydown", onKey);
        return () => { document.body.style.overflow = prev; window.removeEventListener("keydown", onKey); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!current) return null;
    const pct = duration ? (progress / duration) * 100 : 0;
    const fav = isFavorite(current.id);
    const upNext = (queue || []).map((t, i) => ({ t, i })).filter(({ i }) => i > queueIndex).slice(0, 30);

    return (
        <div
            className="fixed inset-0 z-[60] bg-[#050505] text-[#F4F4F4] overflow-y-auto"
            onTouchStart={(e) => { touchY.current = e.touches[0].clientY; }}
            onTouchEnd={(e) => { if (touchY.current != null && e.changedTouches[0].clientY - touchY.current > 140 && window.scrollY === 0) close(); touchY.current = null; }}
            data-testid="full-player"
        >
            {current.cover_url && (
                <img src={current.cover_url} alt="" aria-hidden className="fixed inset-0 w-full h-full object-cover opacity-30 blur-3xl scale-125 pointer-events-none" />
            )}
            <div className="fixed inset-0 bg-gradient-to-b from-black/30 via-black/60 to-black pointer-events-none" />
            <div className="relative max-w-md mx-auto px-6 pt-4 pb-10 min-h-full flex flex-col">
                <div className="flex items-center justify-between">
                    <button onClick={close} className="w-11 h-11 -ml-2 rounded-full flex items-center justify-center hover:bg-white/10" aria-label="Réduire le lecteur" data-testid="full-player-close">
                        <ChevronDown className="w-7 h-7" />
                    </button>
                    <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#A8A8A8]">En lecture</p>
                    <Link to={`/track/${current.slug || current.id}`} onClick={close} className="w-11 h-11 -mr-2 rounded-full flex items-center justify-center hover:bg-white/10" aria-label="Page du titre">
                        <ExternalLink className="w-5 h-5" />
                    </Link>
                </div>

                <div className="mt-6 w-full aspect-square rounded-3xl overflow-hidden shadow-[0_30px_80px_-20px_rgba(0,217,126,0.35)] bg-[#161616]">
                    {current.cover_url ? <img src={current.cover_url} alt={current.title} className="w-full h-full object-cover" /> : <div className="w-full h-full bg-gradient-to-br from-[#1c1c1c] to-[#0F0F0F]" />}
                </div>

                <div className="mt-7 flex items-start justify-between gap-4">
                    <div className="min-w-0">
                        <h2 className="font-display text-2xl font-semibold truncate" data-testid="full-player-title">{current.title}</h2>
                        <p className="mt-1 text-[#A8A8A8] truncate">{current.artist_name}</p>
                    </div>
                    <button onClick={() => toggleFav(current)} className={`shrink-0 w-11 h-11 rounded-full flex items-center justify-center ${fav ? "text-[#ff3d77]" : "text-[#C8C8C8]"} hover:bg-white/10`} aria-label="Favoris">
                        <Heart className={`w-6 h-6 ${fav ? "fill-[#ff3d77]" : ""}`} />
                    </button>
                </div>

                <div className="mt-5">
                    <div className="relative h-6 flex items-center">
                        <div className="absolute inset-x-0 h-1.5 rounded-full bg-white/15 overflow-hidden">
                            <div className="h-full bg-gradient-to-r from-[#ff6b35] via-[#ffc83d] to-[#00d97e]" style={{ width: `${pct}%` }} />
                        </div>
                        <div className="absolute w-4 h-4 rounded-full bg-white shadow" style={{ left: `calc(${pct}% - 8px)` }} />
                        <input type="range" min={0} max={duration || 0} step={0.1} value={progress} onChange={(e) => seek(parseFloat(e.target.value))} className="absolute inset-0 w-full opacity-0 cursor-pointer" aria-label="Position de lecture" />
                    </div>
                    <div className="flex justify-between text-xs text-[#A8A8A8] font-mono tabular-nums">
                        <span>{formatDuration(progress)}</span><span>{formatDuration(duration)}</span>
                    </div>
                </div>

                <div className="mt-4 flex items-center justify-center gap-8">
                    <button onClick={playPrev} disabled={!hasPrev && progress < 3} className="w-16 h-16 rounded-full flex items-center justify-center disabled:opacity-30 hover:bg-white/10" aria-label="Précédent" data-testid="full-player-prev">
                        <SkipBack className="w-8 h-8 fill-current" />
                    </button>
                    <button onClick={toggle} className="w-20 h-20 rounded-full bg-gradient-to-br from-[#00d97e] to-[#00A86B] text-[#0a0a0a] flex items-center justify-center shadow-lg shadow-[#00d97e]/40 active:scale-95 transition-transform" aria-label={playing ? "Pause" : "Lecture"} data-testid="full-player-toggle">
                        {playing ? <Pause className="w-9 h-9 fill-current" /> : <Play className="w-9 h-9 ml-1 fill-current" />}
                    </button>
                    <button onClick={() => playNext(false)} disabled={!hasNext} className="w-16 h-16 rounded-full flex items-center justify-center disabled:opacity-30 hover:bg-white/10" aria-label="Suivant" data-testid="full-player-next">
                        <SkipForward className="w-8 h-8 fill-current" />
                    </button>
                </div>
                <div className="mt-3 flex justify-center"><AddToPlaylistButton track={current} testid="full-player-add-playlist" /></div>

                {upNext.length > 0 && (
                    <div className="mt-8">
                        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#A8A8A8] flex items-center gap-2"><ListMusic className="w-4 h-4" /> À suivre</p>
                        <div className="mt-3 space-y-1">
                            {upNext.map(({ t, i }) => (
                                <button key={`${t.id}-${i}`} onClick={() => playAt(i)} className="w-full flex items-center gap-3 rounded-xl p-2 text-left hover:bg-white/5" data-testid={`full-player-queue-${i}`}>
                                    {t.cover_url ? <img src={t.cover_url} alt="" loading="lazy" className="w-11 h-11 rounded-lg object-cover" /> : <span className="w-11 h-11 rounded-lg bg-[#1c1c1c]" />}
                                    <span className="min-w-0">
                                        <span className="block text-sm font-medium truncate">{t.title}</span>
                                        <span className="block text-xs text-[#8a8a8a] truncate">{t.artist_name}</span>
                                    </span>
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
